/**
 * mx-dropdown.js
 * Custom Element <mx-size-dropdown> para el dropdown de tallas.
 * connectedCallback() se dispara automáticamente cuando quick-add.js
 * inyecta el elemento via cloneNode/morph.
 * Compatible con Ritual theme (product-form-component, on:submit=/handleSubmit).
 */

(() => {
  const LS_KEY = 'mx_recommended_size';

  const getSavedSize = () => {
    try { return (localStorage.getItem(LS_KEY) || '').trim() || null; }
    catch(e) { return null; }
  };

  class MxSizeDropdown extends HTMLElement {

    connectedCallback() {
      if (this._wired) return;

      const cfgEl = this.querySelector('script[type="application/json"][data-mx-config]');
      if (!cfgEl) return;

      let cfg;
      try { cfg = JSON.parse(cfgEl.textContent); }
      catch(e) { return; }

      const {
        optionNames = [],
        variants    = [],
        sizeIndex   = -1,
        hasSize     = false,
        forcePreorder: fpIds = [],
        textAdd         = 'Add to cart',
        textPre         = 'Pre-order',
        textSold        = 'Sold out',
        textFindSize    = 'Find my size',
        textYourSize    = 'Your size is:',
        textEdit        = 'Edit',
        textRecommended = 'Recommended',
      } = cfg;

      const forcePreorder = new Set(fpIds);

      const form = this.closest('form');
      if (!form) return;

      const btn         = this.querySelector('.mx-dd__btn');
      const btnTxt      = this.querySelector('.mx-dd__btn-text');
      const menu        = this.querySelector('.mx-dd__menu');
      const items       = Array.from(this.querySelectorAll('.mx-dd__item'));
      const idInput     = form.querySelector('input[name="id"]');
      const sizeRecoRow = this.querySelector('.mx-dd__size-reco');

      if (!btn || !menu) return;

      this._wired = true;
      this._form  = form;

      const renderSizeRecoRow = () => {
        if (!sizeRecoRow) return;
        const s     = getSavedSize();
        const text  = sizeRecoRow.querySelector('.mx-dd__item-text');
        const badge = sizeRecoRow.querySelector('.mx-dd__badge');
        if (!text || !badge) return;
        if (s) {
          text.textContent  = `${textYourSize} ${s}`;
          badge.hidden      = false;
          badge.textContent = textEdit;
          sizeRecoRow.classList.add('is-size-reco-saved');
        } else {
          text.textContent  = textFindSize;
          badge.hidden      = true;
          badge.textContent = '';
          sizeRecoRow.classList.remove('is-size-reco-saved');
        }
      };

      const openSizeRecommenderDrawer = () => {
        const host = Array.from(document.querySelectorAll('*'))
          .find(el => el.tagName && el.tagName.toLowerCase().startsWith('size-recommender-'));
        if (host) {
          const openBtn = host.querySelector('[data-action="open"]') ||
                          host.querySelector('[data-action="open-result"]');
          if (openBtn) { openBtn.click(); return; }
        }
        window.dispatchEvent(new CustomEvent('mx:open-size-recommender'));
      };

      const getNativeSelectForIndex = (idx) => {
        const name = optionNames[idx];
        return form.querySelector(`select[name="options[${CSS.escape(name)}]"]`);
      };

      const getNativeRadiosForIndex = (idx) => {
        const name = optionNames[idx];
        return form.querySelectorAll(`input[type="radio"][name="options[${CSS.escape(name)}]"]`);
      };

      const currentSelection = (sizeOverride = null) => {
        const sel = new Array(optionNames.length).fill(null);
        optionNames.forEach((name, i) => {
          const el = form.querySelector(`select[name="options[${CSS.escape(name)}]"]`);
          if (el) { sel[i] = el.value || null; return; }
          const checked = form.querySelector(`input[type="radio"][name="options[${CSS.escape(name)}]"]:checked`);
          if (checked) sel[i] = checked.value || null;
        });
        if (hasSize && sizeOverride !== null) sel[sizeIndex] = sizeOverride;
        return sel;
      };

      const findVariant = (sel) =>
        variants.find(v => v.options.every((val, i) => !sel[i] || sel[i] === val));

      const isPreOrder = (v) => {
        if (!v) return false;
        if (forcePreorder.has(v.id)) return true;
        return !!(v.inventory_management && (v.inventory_quantity || 0) <= 0 && v.inventory_policy === 'continue');
      };

      const setBtnCopy = (v) => {
        if (!v || !btnTxt) return;
        btnTxt.textContent = v.available
          ? (isPreOrder(v) ? textPre : textAdd)
          : textSold;
      };

      const syncNativeSize = (sizeVal) => {
        const sel = getNativeSelectForIndex(sizeIndex);
        if (sel) {
          sel.value = sizeVal;
          sel.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }
        const radios = getNativeRadiosForIndex(sizeIndex);
        if (radios && radios.length) {
          const match = Array.from(radios).find(r => r.value === sizeVal);
          if (match) { match.click(); return true; }
        }
        return false;
      };

      const ensureMenuInView = () => {
        menu.classList.remove('mx-dd__menu--up');
        menu.style.maxHeight = '';

        const inQuickAdd = !!menu.closest('#quick-add-modal-content');
        if (!inQuickAdd) {
          const rect = menu.getBoundingClientRect();
          if (rect.bottom > window.innerHeight) {
            window.scrollBy({ top: rect.bottom - window.innerHeight, behavior: 'smooth' });
          }
          return;
        }

        const place = () => {
          let clipTop = -Infinity, clipBottom = Infinity;
          let el = menu.parentElement;
          while (el && el !== document.documentElement) {
            const cs = getComputedStyle(el);
            if (cs.overflow !== 'visible' || cs.overflowY !== 'visible') {
              const r = el.getBoundingClientRect();
              clipTop = Math.max(clipTop, r.top);
              clipBottom = Math.min(clipBottom, r.bottom);
            }
            el = el.parentElement;
          }
          const vv = window.visualViewport;
          const vTop = vv ? vv.offsetTop : 0;
          const vBottom = vTop + (vv ? vv.height : window.innerHeight);
          if (clipTop === -Infinity) clipTop = vTop;
          if (clipBottom === Infinity) clipBottom = vBottom;
          clipTop = Math.max(clipTop, vTop);
          clipBottom = Math.min(clipBottom, vBottom);

          const r = btn.getBoundingClientRect();
          const spaceAbove = r.top - clipTop;
          const spaceBelow = clipBottom - r.bottom;
          const natural = menu.scrollHeight;

          const openUp = (spaceBelow < natural) && (spaceAbove > spaceBelow);
          menu.classList.toggle('mx-dd__menu--up', openUp);

          const avail = (openUp ? spaceAbove : spaceBelow) - 8;
          menu.style.maxHeight = Math.max(120, Math.min(natural, avail)) + 'px';
        };

        place();

        if (window.visualViewport && !menu._mxVVbound) {
          const onVV = () => { if (menu.classList.contains('open')) place(); };
          menu._mxVVhandler = onVV;
          menu._mxVVbound = true;
          window.visualViewport.addEventListener('resize', onVV);
          window.visualViewport.addEventListener('scroll', onVV);
        }
      };

      const refreshMenu = () => {
        const baseSel = currentSelection(null);

        items.forEach(item => {
          const sizeVal = item.dataset.size;
          if (!sizeVal) return;

          const testSel = baseSel.slice();
          testSel[sizeIndex] = sizeVal;
          const v = findVariant(testSel);

          const canPick = !!(v && (v.available || isPreOrder(v)));
          item.disabled = !canPick;

          const badge = item.querySelector('.mx-dd__badge');
          if (badge) {
            if (v && isPreOrder(v)) {
              badge.hidden = false;
              badge.textContent = textPre;
            } else {
              badge.hidden = true;
              badge.textContent = '';
            }
          }
          if (v) item.dataset.variantId = v.id;
          else delete item.dataset.variantId;
        });

        const vNow = findVariant(currentSelection(null));
        if (vNow && idInput) idInput.value = vNow.id;
        setBtnCopy(vNow);

        const saved = getSavedSize();
        items.forEach(item => {
          const sizeVal = item.dataset.size;
          if (!sizeVal) return;
          const badge = item.querySelector('.mx-dd__badge');
          if (!badge) return;
          if (saved && sizeVal.toLowerCase() === saved.toLowerCase()) {
            if (badge.textContent !== textPre) {
              badge.hidden = false;
              badge.textContent = textRecommended;
            }
            item.classList.add('is-recommended');
          } else {
            if (badge.textContent === textRecommended) {
              badge.hidden = true;
              badge.textContent = '';
            }
            item.classList.remove('is-recommended');
          }
        });

        renderSizeRecoRow();
      };

      const openMenu = () => {
        menu.setAttribute('aria-hidden', 'false');
        btn.setAttribute('aria-expanded', 'true');
        menu.classList.add('open');
        ensureMenuInView();
      };

      const closeMenu = () => {
        menu.setAttribute('aria-hidden', 'true');
        btn.setAttribute('aria-expanded', 'false');
        menu.classList.remove('open');
        menu.classList.remove('mx-dd__menu--up');
        menu.style.maxHeight = '';
        if (menu._mxVVbound && menu._mxVVhandler && window.visualViewport) {
          window.visualViewport.removeEventListener('resize', menu._mxVVhandler);
          window.visualViewport.removeEventListener('scroll', menu._mxVVhandler);
          menu._mxVVbound = false;
        }
      };

      this._closeMenu = closeMenu;

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (menu.classList.contains('open')) { closeMenu(); return; }
        refreshMenu();
        openMenu();
      });

      this._onDocClick = (e) => { if (!this.contains(e.target)) closeMenu(); };
      document.addEventListener('click', this._onDocClick);

      if (sizeRecoRow) {
        sizeRecoRow.addEventListener('click', (e) => {
          e.preventDefault();
          openSizeRecommenderDrawer();
          closeMenu();
        });
      }

      items.forEach(item => {
        if (item.classList.contains('mx-dd__size-reco')) return;

        item.addEventListener('click', (e) => {
          e.preventDefault();
          if (item.disabled) return;

          const sizeVal = item.dataset.size;
          syncNativeSize(sizeVal);
          const v = findVariant(currentSelection(sizeVal));
          if (v && idInput) idInput.value = v.id;

          setBtnCopy(v);
          closeMenu();

          if (form) {
            if (form.requestSubmit) form.requestSubmit();
            else form.submit();
          }
        });
      });

      this._onFormChange = (e) => {
        const t = e.target;
        if (t && t.name && t.name.startsWith('options[')) refreshMenu();
      };
      form.addEventListener('change', this._onFormChange);

      this._onStorage = (e) => { if (e.key === LS_KEY) refreshMenu(); };
      window.addEventListener('storage', this._onStorage);

      refreshMenu();
    }

    disconnectedCallback() {
      if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
      if (this._onStorage) window.removeEventListener('storage', this._onStorage);
      if (this._form && this._onFormChange) {
        this._form.removeEventListener('change', this._onFormChange);
      }
      this._wired = false;
      this._onDocClick = this._onStorage = this._onFormChange = this._form = this._closeMenu = null;
    }
  }

  if (!customElements.get('mx-size-dropdown')) {
    customElements.define('mx-size-dropdown', MxSizeDropdown);
  }
})();
